import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trash2, ExternalLink, Loader2, Copy, FileDown } from "lucide-react";
import { toast } from "sonner";

function buildScript(it) {
  const lines = [`# ${it.story.title}`, `Source: ${it.story.source} — ${it.story.url}`, ""];
  if (it.hooks?.length) { lines.push("## OPENING HOOK", it.hooks[0], ""); }
  if (it.talking_points?.length) {
    lines.push("## TALKING POINTS");
    it.talking_points.forEach((t, i) => lines.push(`${i + 1}. ${t}`));
    lines.push("");
  }
  if (it.topics?.length) {
    lines.push("## RELATED TOPICS TO BRIDGE TO");
    it.topics.forEach((t) => lines.push(`- ${t}`));
    lines.push("");
  }
  if (it.titles?.length) {
    lines.push("## TITLE OPTIONS");
    it.titles.forEach((t) => lines.push(`- ${t}`));
  }
  return lines.join("\n");
}

export default function Saved() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/saved");
      setItems(data.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/saved/${id}`);
      setItems((arr) => arr.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Couldn't remove");
    }
  };

  const copyItem = async (it) => {
    try {
      await navigator.clipboard.writeText(buildScript(it));
      toast.success("Script copied to clipboard");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  const downloadPdf = async (it) => {
    setDownloadingId(it.id);
    try {
      const res = await api.get(`/saved/${it.id}/pdf`, { responseType: "blob" });
      const slug = it.story.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "story-packet";
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fount-${slug}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded — ready to print or share");
    } catch {
      toast.error("Couldn't create the PDF. Try again.");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div data-testid="saved-page">
      <header className="border-b border-gray-200 px-8 py-6 sticky top-0 bg-white z-20">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-gray-500">
          Library
        </div>
        <h1 className="font-serif text-4xl lg:text-5xl leading-none tracking-tight mt-2">
          Saved
        </h1>
      </header>

      <div className="px-8 py-6">
        {loading ? (
          <div className="flex items-center gap-3 text-gray-500">
            <Loader2 size={18} className="animate-spin" />
            <span className="font-mono text-xs uppercase tracking-widest">Loading…</span>
          </div>
        ) : items.length === 0 ? (
          <div className="text-gray-500 font-mono text-xs uppercase tracking-widest py-12">
            Nothing saved yet. Generate talking points on a story to save them.
          </div>
        ) : (
          <div className="space-y-6">
            {items.map((it) => (
              <article key={it.id} className="border border-gray-200 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono uppercase tracking-widest text-gray-500 mb-1">
                      {it.story.category} · {new Date(it.saved_at).toLocaleDateString()}
                    </div>
                    <h2 className="font-serif text-2xl tracking-tight leading-snug">
                      {it.story.title}
                    </h2>
                    <a
                      href={it.story.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-[#002FA7] mt-2"
                    >
                      Source <ExternalLink size={12} strokeWidth={1.5} />
                    </a>
                  </div>
                  <button
                    data-testid={`saved-delete-${it.id}`}
                    onClick={() => remove(it.id)}
                    className="text-gray-400 hover:text-red-600"
                    aria-label="Remove"
                  >
                    <Trash2 size={16} strokeWidth={1.5} />
                  </button>
                </div>

                <Block title="Talking points" items={it.talking_points} />
                <Block title="Topics" items={it.topics} />
                <Block title="Hooks" items={it.hooks} />
                <Block title="Titles" items={it.titles} kind="title" />

                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <button
                    data-testid={`saved-copy-${it.id}`}
                    onClick={() => copyItem(it)}
                    className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:border-[#0a0a0a] px-4 py-2 text-xs font-mono uppercase tracking-widest"
                  >
                    <Copy size={14} strokeWidth={1.5} />
                    Copy as script
                  </button>
                  <button
                    data-testid={`saved-pdf-${it.id}`}
                    onClick={() => downloadPdf(it)}
                    disabled={downloadingId === it.id}
                    className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:border-[#0a0a0a] disabled:opacity-60 px-4 py-2 text-xs font-mono uppercase tracking-widest"
                  >
                    {downloadingId === it.id
                      ? <Loader2 size={14} className="animate-spin" />
                      : <FileDown size={14} strokeWidth={1.5} />}
                    {downloadingId === it.id ? "Building PDF…" : "Download PDF"}
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Block({ title, items, kind }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-5">
      <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-2">
        {title}
      </div>
      <ul className="space-y-2">
        {items.map((t, i) => (
          <li
            key={`${t}-${i}`}
            className={`pl-4 border-l-2 border-gray-200 ${
              kind === "title" ? "font-serif text-lg" : "text-sm text-gray-800"
            }`}
          >
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}
